/**
 * UniversalTable Design System
 * 
 * Centralized design tokens for consistent styling across the table component.
 * All magic numbers, colors, and spacing should be defined here.
 */

/**
 * Spacing and padding constants
 */
export const SPACING = {
  /** Header section padding - generous for visual hierarchy */
  header: 'px-8 py-7',
  
  /** Row padding - optimized for readability and touch targets */
  row: 'px-6 py-2.5',
  
  /** Cell padding - balanced spacing */
  cell: 'px-4',
  
  /** Section padding (grouped view, empty state) */
  section: 'p-6',
  
  /** Accordion/group padding */
  group: 'mt-4',
  
  /** Gap between elements */
  gap: {
    small: 'gap-2',
    medium: 'gap-4',
    large: 'gap-6',
  },
} as const;

/**
 * Column width presets (in pixels)
 */
export const COLUMN_WIDTHS = {
  /** Checkbox column */
  checkbox: 40,
  
  /** Small icon columns (insurance, priority icons) */
  icon: 50,
  
  /** Small text columns */
  small: 70,
  
  /** Medium text columns */
  medium: 100,
  
  /** Large text columns */
  large: 150,
  
  /** Extra large text columns */
  xlarge: 200,
  
  /** Action button columns */
  actions: 140,
  
  /** Communication buttons */
  communication: 80,
} as const;

/**
 * Z-index layering system
 */
export const Z_INDEX = {
  /** Base layer for rows */
  row: 0,
  
  /** Sticky header */
  header: 30,
  
  /** Drag indicator for column resizing */
  dragIndicator: 50,
} as const;

/**
 * Color classes for consistent theming
 */
export const COLORS = {
  /** Primary accent color (moss green) */
  accent: '#5B7304',
  
  /** Border colors - refined and subtle */
  border: 'border-neutral-primary/6',
  borderLight: 'border-neutral-primary/4',
  borderMedium: 'border-neutral-primary/10',
  
  /** Hover states - smooth and noticeable */
  hover: '',
  hoverShadow: 'hover:shadow-md',
  
  /** Selected row - clear visual distinction */
  selected: 'bg-moss-primary/10 border-l-moss-primary',
  
  /** Highlighted row (after creation/navigation) - elegant emphasis */
  highlighted: 'ring-2 ring-moss-primary/50 shadow-lg rounded-xl',
  
  /** Header background - subtle gradient for depth */
  headerBg: 'bg-linear-to-b from-neutral-primary/2 to-neutral-primary/4',
  
  /** Column header background - mostly solid with slight transparency */
  columnHeaderBg: 'bg-background',
  
  /** Empty state text */
  emptyText: 'text-neutral-secondary-mid',
  
  /** Row border on left edge - accent indicator */
  rowBorder: 'border-l-[3px] border-l-transparent',
  rowBorderHover: 'hover:border-l-moss-primary/40',
  
  /** Cell border - ultra-subtle separation */
  cellBorder: 'border-r border-neutral-primary/3',
  
  /** Separator border */
  separator: 'divide-y divide-neutral-primary/6',
  
  /** Background colors */
  background: {
    primary: 'bg-background',
    secondary: 'bg-neutral-primary/1.5',
    hover: '',
  },
} as const;

/**
 * Animation and timing constants
 */
export const ANIMATIONS = {
  /** Debounce delay for modal opening (prevents rapid clicks) */
  modalDebounceMs: 50,
  
  /** Transition classes - buttery smooth */
  transition: 'transition-all duration-200 ease-out',
  transitionSlow: 'transition-all duration-350 ease-in-out',
  transitionFast: 'transition-all duration-150 ease-out',
  
  /** Hover effects - subtle and polished */
  hoverTransform: 'transition-shadow duration-200 ease-out',
  hoverScale: 'hover:scale-[1.01] active:scale-[0.99]',
  
  /** Drag resize cursor */
  resizeCursor: 'cursor-col-resize',
  
  /** Interactive states */
  active: 'active:opacity-90 active:scale-[0.98]',
  
  /** Loading states */
  pulse: 'animate-pulse',
  
  /** Focus states - accessible and visible */
  focus: 'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-moss-primary/60 focus-visible:ring-offset-2',
} as const;

/**
 * Responsive breakpoint utilities
 */
export const RESPONSIVE = {
  /** Minimum table width for horizontal scrolling */
  minTableWidth: 900,
  
  /** Hidden until large screens */
  hiddenLg: 'hidden lg:flex',
  
  /** Hidden until extra large screens */
  hiddenXl: 'hidden xl:flex',
  
  /** Block variant for large screens */
  hiddenLgBlock: 'hidden lg:block',
  
  /** Block variant for extra large screens */
  hiddenXlBlock: 'hidden xl:block',
} as const;

/**
 * Typography presets
 */
export const TYPOGRAPHY = {
  /** Header title - prominent hierarchy */
  title: 'text-xl font-bold text-neutral-primary tracking-tight leading-tight',
  
  /** Header description - comfortable reading */
  description: 'text-sm text-neutral-secondary-mid mt-2.5 leading-relaxed',
  
  /** Column headers - clear and scannable */
  columnHeader: 'text-[11px] font-extrabold text-neutral-primary/75 uppercase tracking-wider select-none',
  
  /** Row text - optimal readability */
  rowText: 'text-[13px] text-neutral-primary leading-relaxed',
  
  /** Badge text */
  badge: 'text-sm font-extrabold tracking-tight',
  
  /** Empty state title */
  emptyTitle: 'text-xl font-bold text-neutral-primary mb-3',
  
  /** Empty state description */
  emptyDescription: 'text-sm text-neutral-secondary-mid leading-relaxed mb-8 max-w-md mx-auto',
  
  /** Helper text */
  helper: 'text-xs text-neutral-secondary-mid leading-relaxed',
} as const;

/**
 * Border radius presets - more refined
 */
export const BORDER_RADIUS = {
  /** Table container - softer corners */
  container: 'rounded-2xl',
  
  /** Badge/pill */
  badge: 'rounded-full',
  
  /** Button */
  button: 'rounded-lg',
  
  /** Card elements */
  card: 'rounded-xl',
  
  /** Small elements */
  small: 'rounded-md',
} as const;

/**
 * Shadow presets - refined depth system
 */
export const SHADOWS = {
  /** Table container - elegant elevation */
  container: 'shadow-xl shadow-neutral-primary/4',
  
  /** Header (when sticky) - noticeable depth */
  header: 'shadow-lg shadow-neutral-primary/8',
  
  /** Badge - no shadow for cleaner look */
  badge: '',
  
  /** Drag indicator - high visibility */
  dragIndicator: 'shadow-2xl shadow-moss-primary/25',
  
  /** Row hover - subtle lift */
  rowHover: 'shadow-sm shadow-neutral-primary/4',
  
  /** Interactive elements */
  interactive: 'shadow-sm hover:shadow-md transition-shadow duration-200',
} as const;

/**
 * Layout constraints
 */
export const LAYOUT = {
  /** Maximum rows before showing lazy loading for empty state */
  quickAddThreshold: 5,
  
  /** Grid gap between columns - increased for better visual separation */
  gridGap: 'gap-2',
  
  /** Minimum column width during resize (in pixels) - increased for reliability */
  minColumnWidthPx: 50,
} as const;

/**
 * Cell Content Styling (badges, buttons, interactive elements)
 */
export const CELL_CONTENT = {
  /** Badge styling for labels, tags, status indicators */
  badge: {
    /** Base badge styling */
    base: 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all duration-200 ease-out',
    
    /** Interactive badge (clickable) */
    interactive: 'hover:scale-[1.03] active:scale-[0.97] cursor-pointer focus:ring-2 focus:ring-offset-1',
    
    /** Label/tag badge with icon */
    withIcon: 'gap-1.5',
    
    /** Small compact badge */
    compact: 'px-2 py-0.5 text-[10px] gap-1',
    
    /** Badge with border */
    bordered: 'border border-current/20',
  },
  
  /** Button styling for actions, pickers */
  button: {
    /** Base button styling */
    base: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all duration-200 ease-out',
    
    /** Primary action button */
    primary: 'bg-moss-primary text-white hover:bg-moss-primary/90 shadow-sm hover:shadow-md',
    
    /** Secondary/outline button */
    secondary: 'border border-neutral-primary/20 text-neutral-primary hover:border-moss-primary hover:text-moss-primary hover:bg-moss-primary/5',
    
    /** Danger button */
    danger: 'border border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400',
    
    /** Icon-only button */
    iconOnly: 'p-1.5 rounded-md hover:bg-neutral-primary/5',
    
    /** Interactive states */
    interactive: 'hover:scale-[1.02] active:scale-[0.98] focus:ring-2 focus:ring-moss-primary/30 focus:ring-offset-1',
    
    /** Small/compact button */
    compact: 'px-2 py-1 text-[10px] gap-1',
  },
  
  /** Input styling (text inputs, etc.) */
  input: {
    /** 
     * DEPRECATED: Checkbox styling
     * 
     * Checkboxes now use shadcn/ui Checkbox component with stock Radix UI styling.
     * This old styling is no longer used and kept only for reference.
     * 
     * @deprecated Use shadcn/ui <Checkbox /> component instead
     */
    checkbox: '',
    
    /** 
     * DEPRECATED: Checkbox accent color
     * 
     * @deprecated Checkboxes now use theme colors via shadcn/ui
     */
    checkboxAccent: '',
    
    /** Text input styling */
    text: 'px-3 py-2 text-sm border border-neutral-primary/15 rounded-lg bg-neutral-primary/2 focus:outline-hidden focus:bg-white focus:border-moss-primary focus:ring-2 focus:ring-moss-primary/20 transition-all duration-200 placeholder:text-neutral-secondary-mid',
    
    /** Compact text input styling */
    textCompact: 'px-2.5 py-1.5 text-xs border border-neutral-primary/15 rounded-md bg-neutral-primary/2 focus:outline-hidden focus:bg-white focus:border-moss-primary focus:ring-1 focus:ring-moss-primary/20 transition-all duration-200',
  },
  
  /** Priority/rating elements */
  priority: {
    /** Star size for priority */
    star: 'h-[14px] w-[14px]',
    
    /** Active/filled star */
    active: 'text-brick-primary',
    
    /** Inactive/empty star */
    inactive: 'text-neutral-primary/25',
    
    /** Interactive star */
    interactive: 'cursor-pointer hover:scale-110 active:scale-95 transition-transform duration-150',
  },
  
  /** Text elements within cells */
  text: {
    /** Ticket/entity ID */
    id: 'font-bold text-brick-primary text-[11px] whitespace-nowrap',
    
    /** Title/name */
    title: 'text-[11px] font-medium text-neutral-primary hover:text-blue-600 hover:underline transition-colors cursor-pointer',
    
    /** Title when unread/new */
    titleUnread: 'font-bold',
    
    /** Secondary/meta text */
    meta: 'text-[10px] text-neutral-secondary-mid',
    
    /** Link text */
    link: 'text-blue-600 hover:text-blue-700 hover:underline transition-colors',
  },
  
  /** Icon sizing */
  icon: {
    /** Small icons (in badges, buttons) */
    small: 'h-3 w-3',
    
    /** Medium icons */
    medium: 'h-4 w-4',
    
    /** Large icons */
    large: 'h-5 w-5',
  },
  
  /** Avatar/profile pictures */
  avatar: {
    /** Small avatar */
    small: 'w-5 h-5',
    
    /** Medium avatar */
    medium: 'w-6 h-6',
    
    /** Large avatar */
    large: 'w-8 h-8',
  },
} as const;

/**
 * Complete design system export
 */
export const TABLE_DESIGN = {
  spacing: SPACING,
  columnWidths: COLUMN_WIDTHS,
  zIndex: Z_INDEX,
  colors: COLORS,
  animations: ANIMATIONS,
  responsive: RESPONSIVE,
  typography: TYPOGRAPHY,
  borderRadius: BORDER_RADIUS,
  shadows: SHADOWS,
  layout: LAYOUT,
  cellContent: CELL_CONTENT,
} as const;

/**
 * Type for the design system (useful for extensions)
 */
export type TableDesign = typeof TABLE_DESIGN;

